import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import { useDictation } from './useDictation';

/** Lo mismo que acepta el servidor (api/src/assistant/transcription.ts). */
const FORMATOS = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus', 'audio/ogg'];
const MAX_SEGUNDOS = 90;

function grabadorDisponible(): boolean {
  return typeof window !== 'undefined' && 'MediaRecorder' in window && Boolean(navigator.mediaDevices?.getUserMedia);
}

async function aBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binario = '';
  for (let i = 0; i < bytes.length; i += 0x8000) binario += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binario);
}

/**
 * Dictado de recetas con Whisper (rediseño, 11 oct 2026). Se graba el audio
 * y lo transcribe el servidor: entiende mejor los acentos de México,
 * Colombia o Argentina que el dictado del navegador.
 *
 * Si el servidor no puede (sin clave de OpenAI, como en el sandbox, o una
 * caída), se pasa al dictado del navegador para el resto de la sesión. El
 * texto nunca se envía solo: queda en `transcript` para revisarlo.
 */
export function useDictadoWhisper(locale: string) {
  const navegador = useDictation(locale);
  const [usarNavegador, setUsarNavegador] = useState(!grabadorDisponible());
  const [grabando, setGrabando] = useState(false);
  const [transcribiendo, setTranscribiendo] = useState(false);
  const [segundos, setSegundos] = useState(0);
  const [texto, setTexto] = useState('');
  const [error, setError] = useState<string>();
  const grabador = useRef<MediaRecorder | null>(null);
  const trozos = useRef<Blob[]>([]);

  const soltarMicro = () => grabador.current?.stream.getTracks().forEach((t) => t.stop());

  const parar = useCallback(() => {
    if (usarNavegador) { navegador.stop(); return; }
    if (grabador.current?.state === 'recording') grabador.current.stop();
  }, [usarNavegador, navegador.stop]);

  const empezar = useCallback(async () => {
    setError(undefined);
    setTexto('');
    if (usarNavegador) { navegador.start(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = FORMATOS.find((f) => MediaRecorder.isTypeSupported(f));
      const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      grabador.current = rec;
      trozos.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) trozos.current.push(e.data); };
      rec.onstop = async () => {
        soltarMicro();
        setGrabando(false);
        const blob = new Blob(trozos.current, { type: rec.mimeType || mimeType || 'audio/webm' });
        if (!blob.size) { setError('No se oyó nada. Prueba otra vez o escríbelo.'); return; }
        setTranscribiendo(true);
        try {
          const { text } = await api.transcribeRecipeAudio({ audioBase64: await aBase64(blob), mimeType: blob.type.split(';')[0] });
          setTexto(text);
        } catch {
          // Sin Whisper: el resto de la sesión se dicta con el navegador, si puede.
          const puede = navegador.supported;
          setUsarNavegador(puede);
          setError(puede ? 'No pude pasar la grabación a texto. Vuelve a tocar el botón: esta vez te escucho de otra forma.' : 'No pude pasar la grabación a texto. Escríbelo, por favor.');
        } finally {
          setTranscribiendo(false);
        }
      };
      rec.start();
      setSegundos(0);
      setGrabando(true);
    } catch {
      // Sin grabador utilizable: se prueba con el dictado del navegador.
      if (navegador.supported) { setUsarNavegador(true); navegador.start(); }
      else setError('Activa el permiso del micrófono o escribe tu plato.');
    }
  }, [usarNavegador, navegador.start, navegador.supported]);

  useEffect(() => {
    if (!grabando) return undefined;
    const t = window.setInterval(() => setSegundos((s) => s + 1), 1000);
    return () => window.clearInterval(t);
  }, [grabando]);
  useEffect(() => { if (grabando && segundos >= MAX_SEGUNDOS) parar(); }, [grabando, segundos, parar]);
  useEffect(() => () => { if (grabador.current?.state === 'recording') grabador.current.stop(); soltarMicro(); }, []);

  return {
    supported: !usarNavegador || navegador.supported,
    listening: usarNavegador ? navegador.listening : grabando,
    transcribing: transcribiendo,
    seconds: usarNavegador ? undefined : segundos,
    maxSeconds: MAX_SEGUNDOS,
    transcript: usarNavegador ? navegador.transcript : texto,
    error: error ?? navegador.error,
    start: () => { void empezar(); },
    stop: parar,
  };
}
